import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Hook return type
 */
export interface UseDebouncedInputReturn {
    localValue: string;
    handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    setValue: (value: string) => void;
}

/**
 * Reusable hook for debounced text input handling
 * Updates local state immediately but debounces the callback
 *
 * @param initialValue - Initial value for the input
 * @param onDebouncedChange - Callback executed after debounce delay
 * @param delay - Debounce delay in milliseconds (default: 2000)
 * @returns Debounced input utilities and state
 */
export const useDebouncedInput = (
    initialValue: string,
    onDebouncedChange: (value: string) => void,
    delay: number = 2000
): UseDebouncedInputReturn => {
    const [localValue, setLocalValue] = useState<string>(initialValue);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (initialValue !== localValue) {
            setLocalValue(initialValue);
        }
    }, [initialValue]);

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setLocalValue(newValue);

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            onDebouncedChange(newValue);
        }, delay);
    }, [onDebouncedChange, delay]);

    const setValue = useCallback((value: string) => {
        setLocalValue(value);
    }, []);

    return {
        localValue,
        handleChange,
        setValue
    };
};
